import * as React from 'react'
import Select from 'react-select'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { CurrentUserStore } from '../../../../../stores/CurrentUserStore'
import { FormError } from '../../../../../components/FormError'
import { BlazeButton } from '../../../../../components/CustomButtons/BlazeButton'

interface State {
  locationId: number | null
  key: string | null
  secret: string | null
  url: string | null
  autoSync: boolean
  errorMessage?: string
  isLoading: boolean
}

interface Props {
  currentUserStore: CurrentUserStore
  currentCompanyStore: CurrentCompanyStore
}

interface ISelectedLocationResult {
  label: string
  value: number
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class WooCommerceSettings extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      locationId: null,
      key: null,
      secret: null,
      isLoading: false,
      url: null,
      autoSync: false,
    }
  }

  componentDidMount() {
    this.props.currentCompanyStore!.getLocations()
    this.setValues()
  }

  setValues() {
    const company = this.props.currentCompanyStore!.company!
    this.setState({
      locationId: company.wooCommerceLocationId,
      key: company.wooCommerceKey,
      secret: company.wooCommerceSecret,
      url: company.wooCommerceUrl,
      autoSync: company.isWooCommerceAutoSync,
    })
  }

  save() {
    this.setState({
      errorMessage: undefined,
      isLoading: true,
    })
    this.props
      .currentCompanyStore!.update({
        wooCommerceLocationId: this.state.locationId,
        wooCommerceUrl: this.state.url || null,
        wooCommerceKey: this.state.key || null,
        wooCommerceSecret: this.state.secret || null,
        isWooCommerceAutoSync: this.state.autoSync,
      })
      .then(() => {
        this.setState({
          isLoading: false,
        })
        this.setValues()
      })
      .catch((error) => {
        this.setState({
          isLoading: false,
          errorMessage: error.response.data.message || 'There was an error',
        })
      })
  }

  render() {
    const company = this.props.currentCompanyStore!.company!
    const locations = this.props.currentCompanyStore!.locations

    if (!locations) {
      return <div>Loading...</div>
    }

    const paymentWebhookUrl = `${process.env.REACT_APP_ROOT_API_URL}/api/v2/woocommerce/companies/${company.id}/order`
    const parkedSaleWebhookUrl = `${process.env.REACT_APP_ROOT_API_URL}/api/v2/woocommerce/companies/${company.id}/order/pickup`

    return (
      <div className='row'>
        <div className='col-md-6'>
          <div className='form-group'>
            <div>Inventory location</div>
            <div className='text-muted mb-2'>Your online store will sync with this location</div>
            <Select
              isClearable
              value={this.state.locationId}
              options={
                locations.map((location) => {
                  return {
                    value: location.id,
                    label: location.name,
                  }
                }) as ISelectedLocationResult[]
              }
              onChange={(result: ISelectedLocationResult) => {
                this.setState({ locationId: result ? result.value : null })
              }}
            />
          </div>
          <br />
          <div className='form-group'>
            <div>Website URL</div>
            <div className='text-muted'>Formatted as https://yourwebsite.com</div>
            <div className='text-muted mb-2'>HTTPS security required</div>
            <input
              className='form-control'
              value={this.state.url || ''}
              onChange={(e) => this.setState({ url: e.target.value })}
            />
          </div>
          <br />
          <div className='form-group'>
            <div>Webhook URL</div>
            <div className='text-muted mb-2'>
              Copy/paste this into your WooCommerce API settings page
            </div>
            <div>
              Paid online (will show up under sales): <u>{paymentWebhookUrl}</u>
            </div>
            <div>
              Pay in-store (will show up under parked sales): <u>{parkedSaleWebhookUrl}</u>
            </div>
          </div>
          <br />
          <div className='form-group'>
            <div>WooCommerce API key</div>
            <div className='text-muted mb-2'>
              Find this value from your WooCommerce API settings page
            </div>
            <input
              className='form-control'
              value={this.state.key || ''}
              onChange={(e) => this.setState({ key: e.target.value })}
            />
          </div>
          <br />
          <div className='form-group'>
            <div>WooCommerce API secret</div>
            <div className='text-muted mb-2'>
              Find this value from your WooCommerce API settings page
            </div>
            <input
              className='form-control'
              value={this.state.secret || ''}
              onChange={(e) => this.setState({ secret: e.target.value })}
            />
          </div>
          <br />
          <div className='form-group'>
            <div>Product sync settings</div>
            <div className='text-muted mb-2'>
              Note: Inventory will always be synced in real-time once the product is synced.
            </div>
            <div>
              <input
                className='mr-2'
                type='checkbox'
                checked={this.state.autoSync}
                onChange={() => this.setState({ autoSync: true })}
              />
              Sync all products in WooCommerce categories
            </div>
            <div>
              <input
                className='mr-2'
                type='checkbox'
                checked={!this.state.autoSync}
                onChange={() => this.setState({ autoSync: false })}
              />
              Pick which products you want synced
            </div>
          </div>
          <br />
          {this.state.errorMessage !== undefined && <FormError message={this.state.errorMessage} />}
          <div className='form-group'>
            <BlazeButton
              buttonProps={{
                type: 'primary',
                size: 'large',
                disabled: this.state.isLoading,
                onClick: () => this.save(),
              }}
              titleProps={{
                title: this.state.isLoading ? 'Saving...' : 'Save',
              }}
            />
          </div>

          <br />
          <br />
        </div>
      </div>
    )
  }
}
